import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import React from 'react';

import { IconCheckbox, IconInfoCircle } from '@tabler/icons-react';
import { uploadConfig } from 'app-level/config/uploadConfig';
import { useFeatureFlag } from 'context/FeatureFlagContext';
import { PillFilters } from 'features/common-elements';
import { LoanDatatableLoan } from 'features/drilldown/LoanDatatable/__generated__/fragments';
import { useDealRoomContext } from 'features/pages/marketplace/DealRoomContext';
import FileUpload from 'features/pages/portfolio/DocumentLibrary/FileUpload/FileUpload';
import { useFilterConfig } from 'features/pages/portfolio/Portfolio/portfolioFilters.config';
import { TableQueryParameters } from 'hooks/useTableQueryParams';
import toast from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import {
  ModalNotStyled,
  Button,
  Dropdown,
  FileUploadProgressBar,
  LinkButton,
  useDialog,
} from 'ui-kit';
import DealButton from 'ui-kit/DealButton/DealButton';
import { formatTimeDifference } from 'utils/dateUtils';

import { useMutation, useQuery } from '@apollo/client';

import { AssetClass, DealRole, ParentType, FileType } from '__generated__/globalTypes';
import {
  MARK_COLLATERAL_REVIEW_COMPLETE,
  REFETCH_DEAL_DILIGENCE_QUERIES,
} from 'mutation/diligenceMutations';
import { GET_COLLATERAL_REPORT_URL } from 'mutation/getCollateralReportUrl';

import { GetDealDiligence } from 'query/__generated__/diligence';
import { GET_DILIGENCE_AI, GetDealDiligence_deal_diligence } from 'query/diligence';
import LIST_COLLATERAL_REPORTS from 'query/getCollateralReport';

import DiligenceLoanDocumentDrawer, { DocumentsProps } from './DiligenceLoanDocumentDrawer';
import DiligencePopulationMetrics, {
  DiligencePopulationMetricsProps,
} from './DiligencePopulationMetrics';
import { DiligenceStatus } from './DiligenceStatus';
import { FileDiligenceStatus } from './FileStatus';
import { LoanDiligenceStatus } from './LoanDiligenceStatus';
import { QuickStatusDialog } from './QuickStatusDialog';
import ReconciliationReport from './ReconciliationReport';
import { ReviewDiligenceTable } from './ReviewDiligenceTable';

import { ListCollateralReports } from 'query/__generated__/getCollateralReport';
type ListCollateralReports_listCollateralReports_reports = NonNullable<
  NonNullable<NonNullable<ListCollateralReports>['listCollateralReports']>['reports']
>[number];
const aiPoolInterval = 10000;
/** When AI is idle, still poll occasionally so a new zip upload can surface `diligenceInProgress`. */
const aiIdlePollIntervalMs = 30_000;

const ProgressMessage = memo(function ProgressMessage({
  isDiligenceInProgress,
  error,
}: {
  isDiligenceInProgress: boolean;
  error?: string | null;
}) {
  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-400">
        <IconInfoCircle
          stroke={2}
          size={24}
          className="text-red-400"
          data-tooltip-id="error-tooltip"
        />
        <span>There was an error with your data.</span>
        <Tooltip id="error-tooltip" className="max-w-[300px]" place="left">
          <p>
            There was an error with your data. Our Engineering team was notified and is actively
            fixing the issue now.
          </p>
        </Tooltip>
      </div>
    );
  }

  const iconColorClass = isDiligenceInProgress ? 'text-blue-400' : 'text-yellow-400';
  const tooltipId = isDiligenceInProgress ? 'ai-diligence' : 'processing-zip';
  const tooltipText = isDiligenceInProgress
    ? 'The AI diligence checks are currently being performed on your uploaded documents.'
    : 'Documents are being processed and matched to loans. This may take a few minutes.';

  return (
    <div className="flex items-center">
      <IconInfoCircle stroke={2} size={24} className={iconColorClass} data-tooltip-id={tooltipId} />
      <Tooltip id={tooltipId} className="max-w-[300px]" place="left">
        <p>{tooltipText}</p>
      </Tooltip>
    </div>
  );
});

export interface ReviewDiligenceProps {
  dealId: string;
  dealName: string;
  assetClass: AssetClass;
  role: DealRole;
  isTabActive: boolean;
  reviewDiligenceMetrics: DiligencePopulationMetricsProps;
  should_show_summaries_tab: boolean;
  queryParams: TableQueryParameters<any, any>;
  updateQueryParams: (params: Partial<TableQueryParameters<any, any>>) => void;
  totalDiligenceItems?: number;
  diligenceData: GetDealDiligence_deal_diligence[];
  diligenceDeal: GetDealDiligence;
  handleLoanDiligenceStatusChanged: (loanId: string, status: LoanDiligenceStatus) => void;
  documents: DocumentsProps; // Collateral documents
  userId: string;
}

export const ReviewDiligenceTab: React.FC<ReviewDiligenceProps> = ({
  dealId,
  dealName,
  reviewDiligenceMetrics,
  role,
  isTabActive,
  queryParams,
  updateQueryParams,
  totalDiligenceItems,
  assetClass,
  diligenceData,
  diligenceDeal,
  handleLoanDiligenceStatusChanged,
  documents,
  userId,
  should_show_summaries_tab,
}) => {
  const { setDocumentSelectedForView } = useDealRoomContext();
  const { deal, setActionTargetLoanId, selectedDiligenceForDialog } =
    useReviewDiligenceTab(diligenceDeal);

  const portfolioFiltersConfig = useFilterConfig(assetClass, {
    parentType: ParentType.DEAL,
    parentId: dealId,
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [drawerLoanId, setDrawerLoanId] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [uploadFileName, setUploadFileName] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const [fileUploadProgress, setFileUploadProgress] = useState<number>(0);

  const isCollateralZipReportEnabled = useFeatureFlag('collateralZipReport');

  const {
    data: aiDiligenceData,
    refetch: refetchAiDiligence,
    startPolling: startAiPolling,
    stopPolling: stopAiPolling,
  } = useQuery(GET_DILIGENCE_AI, {
    variables: { input: { dealId } },
    skip: role !== DealRole.SELLER,
  });

  const diligenceAiInProgress =
    aiDiligenceData?.getDiligenceAI?.processingStatus?.diligenceInProgress === true;

  useEffect(() => {
    if (role !== DealRole.SELLER || !isTabActive) {
      stopAiPolling();
      return;
    }
    startAiPolling(diligenceAiInProgress ? aiPoolInterval : aiIdlePollIntervalMs);
    return () => {
      stopAiPolling();
    };
  }, [role, isTabActive, diligenceAiInProgress, startAiPolling, stopAiPolling]);

  const [markCollateralReviewComplete, { loading: markingComplete }] = useMutation(
    MARK_COLLATERAL_REVIEW_COMPLETE,
    {
      refetchQueries: ['GetTimeline', 'GetDealGeneralInfo', ...REFETCH_DEAL_DILIGENCE_QUERIES],
      onCompleted: () => {
        toast.success('Collateral review marked as complete!');
      },
    },
  );

  const { data: reportsData, loading: loadingReports } = useQuery(LIST_COLLATERAL_REPORTS, {
    variables: { dealId },
    skip: role !== DealRole.SELLER,
  });

  const [getReportUrl] = useMutation(GET_COLLATERAL_REPORT_URL);

  useEffect(() => {
    if (aiDiligenceData?.getDiligenceAI?.processingStatus) {
      const { activeZipFileName, customerPercentage } =
        aiDiligenceData.getDiligenceAI.processingStatus;

      if (activeZipFileName) {
        setFileName(activeZipFileName);
      }

      setProgress(customerPercentage || 0);
    } else {
      setFileName('');
      setProgress(0);
    }
  }, [aiDiligenceData]);

  const handleDownloadReport = async (zipFileName: string) => {
    try {
      const { data } = await getReportUrl({
        variables: {
          input: { dealId, zipFileName },
        },
      });

      if (data?.getCollateralReportUrl?.url) {
        window.open(data.getCollateralReportUrl.url, '_blank');
      }
    } catch (e) {
      toast.error('Failed to get download URL');
      console.log(e);
    }
  };

  const quickStatusDialog = useDialog();

  const handleQuickStatusChange = (
    dealId: string,
    loanId: string,
    fileName: string,
    status: FileDiligenceStatus,
  ) => {
    documents.onFileStatusSelect(dealId, loanId, fileName, status);
  };

  const handleQuickStatusClick = (loanId: string) => {
    setActionTargetLoanId(loanId);
    quickStatusDialog.openDialog();
  };

  const handleViewClick = (loanId: string) => {
    setDrawerLoanId(loanId);
    setIsDrawerOpen(true);
  };

  const handleLoanSelectedInDrawer = (loanId: string) => {
    setDrawerLoanId(loanId);
  };

  const handleCloseDrawer = () => {
    setDocumentSelectedForView(false);
    setIsDrawerOpen(false);
  };

  const handleFileSuccess = (file?: File) => {
    if (role === DealRole.SELLER) {
      void refetchAiDiligence();
    }
    toast.success(
      <div className="flex flex-col">
        <p>Upload was successful</p>
        <p>Your .zip '{file?.name}' was successfully uploaded.</p>
      </div>,
      {
        duration: 4000,
        position: 'bottom-right',
      },
    );
  };

  const onFileUploadProgress = (progress: number, file?: File) => {
    setFileUploadProgress(progress);
    setUploadFileName(file?.name || '');
  };

  const sections = [
    {
      title: 'Document Completeness',
      data: [
        { label: 'Loan App', percentage: '99.998%', value: '23,220/23,223' },
        { label: 'Third Party Appraisal', percentage: '0%', value: '0/23,223' },
        {
          label: 'Document Name',
          percentage: '95.998%',
          value: '23,220/23,223',
        },
      ],
    },
    {
      title: 'Key Fields',
      data: [
        { label: 'FICO', percentage: '99.998%', value: '23,220/23,223' },
        {
          label: 'DTI at Origination',
          percentage: '99.998%',
          value: '23,220/23,223',
        },
      ],
    },
    {
      title: '',
      data: [
        {
          label: 'Interest Rate',
          percentage: '95.998%',
          value: '23,220/23,223',
        },
        {
          label: 'LTV at Origination',
          percentage: '89.99%',
          value: '23,220/23,223',
        },
      ],
    },
  ];

  const renderZipUploadModal = (trigger: React.ReactElement) => {
    return (
      <ModalNotStyled trigger={trigger}>
        {({ closeModal }) => (
          <div className="w-full max-w-[647px] rounded-lg border border-pink-500 bg-background-canvas p-6 font-heebo text-white shadow-lg">
            <div className="flex items-center justify-end">
              <button className="text-gray-500 hover:text-gray-300" onClick={closeModal}>
                ✕
              </button>
            </div>
            <h2 className="mt-2 flex justify-center text-xl font-semibold">
              Collateral Batch Upload
            </h2>
            <p className="mb-8 mt-8 flex justify-center text-center">
              Each collateral document will be processed and mapped to each respective Loan ID.
            </p>

            <FileUpload
              parentId={dealId}
              fileType={FileType.OTHER}
              allowedFileTypes={uploadConfig.diligenceZip.allowedFileTypes}
              onFileSuccess={(file) => handleFileSuccess(file)}
              width="auto"
              height="auto"
              showButtons={false}
              timeToHideProgress={2}
              isCollateral={true}
              vertical={true}
              allowedTypesMessage={uploadConfig.diligenceZip.message}
              onProgressCallback={onFileUploadProgress}
              showProgress={false}
              uploadedBy={userId}
              disabled={!!fileName}
            />

            {(uploadFileName || fileName) && (
              <div className="mt-4 flex flex-col items-center border-t border-slate-500 p-4">
                {fileUploadProgress < 100 && !fileName ? (
                  <FileUploadProgressBar
                    fileName={uploadFileName}
                    progress={fileUploadProgress}
                    width="100%"
                  />
                ) : (
                  <div className="mt-2 text-green-500">File uploaded successfully!</div>
                )}
              </div>
            )}
          </div>
        )}
      </ModalNotStyled>
    );
  };

  // TODO: need to add fetch data for it
  const showReconciliationReport = false;

  const generateReport = () => {
    return (
      <Dropdown placeholder="View Reports" menuClasses="w-[384px] top-9">
        <div className="p-3">
          <p className="flex justify-start font-heebo">Generated reports</p>
          <span className="mb-[10px] mt-3 text-xs text-slate-400">
            Total: {reportsData?.listCollateralReports?.reports?.length || 0} Reports
          </span>
          <div>
            {reportsData?.listCollateralReports?.reports?.map(
              (report: ListCollateralReports_listCollateralReports_reports) => (
                <div
                  key={report.reportTitle || report.zipFileName}
                  className="mb-3 flex items-center justify-between text-xs"
                >
                  <span className="w-[160px] text-gray-50">
                    {report.reportTitle || report.zipFileName}
                  </span>
                  <span className="text-gray-600">
                    {report.uploadTimestamp
                      ? formatTimeDifference(Number(report.uploadTimestamp))
                      : 'N/A'}
                  </span>
                  <LinkButton
                    text="Download"
                    url="#"
                    onClick={() => handleDownloadReport(report.zipFileName)}
                    contentClassName="text-pink-400 font-bold"
                  />
                </div>
              ),
            )}
          </div>
        </div>
      </Dropdown>
    );
  };

  const showReport =
    !loadingReports && isCollateralZipReportEnabled && reportsData?.listCollateralReports;

  return (
    <div className="relative">
      {should_show_summaries_tab && (
        <div
          className="absolute inset-0 z-10 bg-black bg-opacity-20"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <div className="mb-8">
        {showReconciliationReport && <ReconciliationReport sections={sections} />}
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex gap-6">
          <div className="text-[12px] font-normal leading-[20px] tracking-[0] text-gray-600 ">
            Diligence Population
          </div>
          <DiligencePopulationMetrics metrics={reviewDiligenceMetrics} />
        </div>
        <div className="flex gap-6">
          <div className="text-[12px] font-normal leading-[20px] tracking-[0] text-gray-600 ">
            Collateral File Status
          </div>
          <DiligenceStatus diligenceData={diligenceData} />
        </div>
        <div className="flex gap-6">
          <div className="flex w-full justify-between">
            <div>
              {portfolioFiltersConfig && (
                <PillFilters
                  filtersConfig={portfolioFiltersConfig}
                  currentFilters={queryParams.filters}
                  setCurrentFilters={(filters) => updateQueryParams({ ...queryParams, filters })}
                />
              )}
            </div>
          </div>
        </div>
        {role === DealRole.BUYER && (
          <div className="flex justify-end gap-6">
            <ModalNotStyled
              trigger={
                <DealButton
                  label="Mark Collateral Review Complete"
                  Icon={<IconCheckbox />}
                  iconPosition="right"
                  disabled={should_show_summaries_tab}
                  variant="success_secondary"
                >
                  Mark Collateral Review Complete
                </DealButton>
              }
              dismissable={!markingComplete}
            >
              {({ closeModal }) => (
                <div className="w-full max-w-[500px] rounded-lg border border-pink-500 bg-background-canvas p-6 font-heebo text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">Mark Collateral Review Complete</h2>
                    <button
                      className="text-gray-500 hover:text-gray-300"
                      onClick={closeModal}
                      disabled={markingComplete}
                    >
                      ✕
                    </button>
                  </div>
                  <p className="my-4">
                    Are you sure you are finished reviewing the collateral and ready to see the
                    final sum?
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button onClick={closeModal} label="Cancel" disabled={markingComplete}>
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          await markCollateralReviewComplete({
                            variables: {
                              input: {
                                deal_id: dealId,
                              },
                            },
                          });
                          closeModal();
                        } catch (error) {
                          const message = error instanceof Error ? error.message : 'Unknown error';
                          toast.error(`Error marking collateral review complete: ${message}`);
                        }
                      }}
                      label="Confirm"
                      className="bg-purple-600 text-white"
                      disabled={markingComplete}
                    >
                      {markingComplete ? 'Submitting...' : 'Confirm'}
                    </Button>
                  </div>
                </div>
              )}
            </ModalNotStyled>
          </div>
        )}
        {role === DealRole.SELLER && (
          <div className="flex justify-between gap-6">
            {showReport ? generateReport() : <div />}
            <div className="flex items-center gap-4">
              {fileName && (
                <>
                  <ProgressMessage
                    isDiligenceInProgress={
                      !!aiDiligenceData?.getDiligenceAI?.processingStatus?.diligenceInProgress
                    }
                    error={aiDiligenceData?.getDiligenceAI?.processingStatus?.error}
                  />
                  {!aiDiligenceData?.getDiligenceAI?.processingStatus?.error && (
                    <FileUploadProgressBar fileName={fileName} progress={progress * 100} />
                  )}
                </>
              )}
              {!fileName &&
                renderZipUploadModal(
                  <Button
                    type="secondary"
                    size="compact"
                    label="Batch Upload"
                    className="px-[6px] py-[2px]"
                  >
                    Batch Upload: Collateral Files (.zip)
                  </Button>,
                )}
            </div>
          </div>
        )}

        <ReviewDiligenceTable
          role={role}
          onLoanDiligenceStatusChanged={handleLoanDiligenceStatusChanged}
          deal={deal}
          assetClass={assetClass}
          sorting={{
            state: queryParams.sortings,
            onSortingChanged: (newSortings) => {
              updateQueryParams({ sortings: newSortings });
            },
          }}
          onViewClick={handleViewClick}
          onQuickStatusClick={handleQuickStatusClick}
          pagination={{
            updateParams: updateQueryParams,
            queryParams,
            totalItems: deal?.diligence_performance_summary?.loanCount || 0,
          }}
        />
      </div>

      <QuickStatusDialog
        loanName={selectedDiligenceForDialog?.loan.account_id || 'Unknown Loan'}
        collateral={selectedDiligenceForDialog?.collateral || []}
        dialog={quickStatusDialog}
        role={role}
        onStatusChange={handleQuickStatusChange}
      />
      {isDrawerOpen && (
        <>
          <div className="fixed inset-0 z-10 bg-black bg-opacity-50" onClick={handleCloseDrawer} />
          <div
            className={`fixed right-0 top-0 z-20 h-full w-[1070px] transform bg-background-surface shadow-lg transition-transform duration-300 ease-in-out ${
              isDrawerOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <DiligenceLoanDocumentDrawer
              role={role}
              dealName={dealName}
              diligenceSelector={{
                diligence: diligenceData,
                selectedLoanId: drawerLoanId,
                onSelectLoan: handleLoanSelectedInDrawer,
              }}
              documents={documents}
              userId={userId}
              dealId={dealId}
            />
          </div>
        </>
      )}
    </div>
  );
};

const useReviewDiligenceTab = (data: GetDealDiligence) => {
  const [actionTargetLoanId, setActionTargetLoanId] = useState<string | null>(null);

  const diligenceLoans = useMemo(
    () => (data?.deal?.diligence || []).map((diligence) => diligence.loan as LoanDatatableLoan),
    [data?.deal?.diligence],
  );

  const selectedDiligenceForDialog = useMemo(
    () =>
      (data?.deal?.diligence || []).find((diligence) => diligence.loan.id === actionTargetLoanId),
    [data?.deal?.diligence, actionTargetLoanId],
  );

  const setActionTargetLoanIdCallback = useCallback((id: string | null) => {
    setActionTargetLoanId(id);
  }, []);

  return useMemo(
    () => ({
      deal: data?.deal,
      diligenceLoans,
      selectedDiligenceForDialog,
      actionTargetLoanId,
      setActionTargetLoanId: setActionTargetLoanIdCallback,
    }),
    [
      data?.deal,
      diligenceLoans,
      selectedDiligenceForDialog,
      actionTargetLoanId,
      setActionTargetLoanIdCallback,
    ],
  );
};
